import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountExtensionView } from '../../add-account-extension/models/account-extension-view.model';
import { ExtensionInfoService } from '../services/extension-info.service';

@Component({
    providers: [ExtensionInfoService],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-account-extensions',
    styleUrls: ['./account-extensions.component.scss'],
    templateUrl: './account-extensions.component.html',
    standalone: false
})
export class AccountExtensionsComponent implements ControlValueAccessor, OnDestroy {
   @Output() onEditClicked = new EventEmitter<AccountExtensionView>();

   @Output() removeExtension = new EventEmitter<number>();

   accountExtensionItems: AccountExtensionView[];

   disabled = false;

   selectedExtensionNumber: number | null;

   private destroyed$ = new Subject<void>();

   onTouched: () => void;

   private onChange: (value: Array<AccountExtensionView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private extensionInfoService: ExtensionInfoService
   ) {
      control.valueAccessor = this;
      this.extensionInfoService.outsidePointerEvent$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         setTimeout(() => {
            this.selectedExtensionNumber = null;
            this.changeDetectorRef.markForCheck();
         }, 0);
      });
   }

   writeValue(extensionItems: AccountExtensionView[] | null): void {
      if (extensionItems) {
         this.accountExtensionItems = extensionItems.sort((ext1, ext2) => {
            if (!ext1.number && !ext2.number) {
               return 0;
            }

            if (!ext1.number || ext1.number > (ext2.number ?? 0)) {
               return 1;
            }

            if (!ext2.number || ext1.number < ext2.number) {
               return -1;
            }

            return 0;
         });
      }
      this.changeDetectorRef.markForCheck();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   registerOnChange(onChange: (value: AccountExtensionView[] | null) => void): void {
      this.onChange = onChange;
   }
   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState(disabled: boolean): void {
      this.disabled = disabled;
      this.changeDetectorRef.markForCheck();
   }

   onDetailsClick(extension: AccountExtensionView, button: HTMLElement): void {
      if (!this.selectedExtensionNumber) {
         this.extensionInfoService.showExtensionInfo(extension, button, this.changeDetectorRef);
         setTimeout(() => {
            this.selectedExtensionNumber = extension.number ?? null;
            this.changeDetectorRef.markForCheck();
         }, 0);
      }
   }

   onRemoveClick(extension: AccountExtensionView) {
      this.removeExtension.emit(extension.number ?? null);
   }

   onEditClick(extension: AccountExtensionView) {
      this.onEditClicked.emit(extension);
   }
}
